import Backbone from 'backbone';
import _ from 'underscore';

import BenchlingCollection from 'BenchlingCollection';
import {ServerFragmentResult} from 'constants/SequenceConstants';
import {shuffledPastelColors} from 'lib/Colors';
import {DigestableEnzyme} from 'molbio/enzymes/EnzymeConstants';

interface FragmentResultAttributes {
  end: number | null;
  endEnzymeID: number | null;
  fragmentID: number | null;
  index: number | null;
  originalBases: string | null;
  pairIndex?: number;
  parent: {id: string; name: string} | null;
  parentVersion: {created_at: string; id: number} | null;
  primerPairID: number | null;
  start: number | null;
  startEnzymeID: number | null;
  versionId: number | null;
  start_sticky_end: string | null;
  end_sticky_end: string | null;
  _cutter5?: DigestableEnzyme;
  _cutter3?: DigestableEnzyme;
  color?: string | null;
  homologyRegionOtherColor?: string | null;
}

export interface FragmentResult {
  get<K extends keyof FragmentResultAttributes>(key: K): FragmentResultAttributes[K];
}

/** Fragment model passed to Sequence.selectFragment (digest slices or fragment results). */
export interface SelectableFragment {
  cid: string;
  get(key: 'start'): number | null | undefined;
  get(key: 'end'): number | null | undefined;
  get(key: '_cutter5'): DigestableEnzyme | undefined;
  get(key: '_cutter3'): DigestableEnzyme | undefined;
  set(attrs: {selected: boolean}): void;
}

export class FragmentResult extends Backbone.Model {
  override defaults(): FragmentResultAttributes {
    return {
      end: null,
      endEnzymeID: null,
      fragmentID: null,
      index: null,
      originalBases: null,
      parent: null,
      parentVersion: null,
      primerPairID: null,
      start: null,
      startEnzymeID: null,
      versionId: null,
      start_sticky_end: null,
      end_sticky_end: null,
      color: null,
      homologyRegionOtherColor: null,
    };
  }

  start(): FragmentResultAttributes['start'] {
    return this.get('start');
  }

  end(): FragmentResultAttributes['end'] {
    return this.get('end');
  }

  color(): FragmentResultAttributes['color'] {
    if (this.get('color') != null) {
      return this.get('color');
    } else if (this.index() != null) {
      return shuffledPastelColors[this.index()! % shuffledPastelColors.length];
    }
    return null;
  }

  homologyRegionOtherColor(): FragmentResultAttributes['homologyRegionOtherColor'] {
    return this.get('homologyRegionOtherColor');
  }

  index(): FragmentResultAttributes['index'] {
    return this.get('index');
  }

  primerPairID(): FragmentResultAttributes['primerPairID'] {
    return this.get('primerPairID');
  }

  startEnzymeID(): FragmentResultAttributes['startEnzymeID'] {
    return this.get('startEnzymeID');
  }

  endEnzymeID(): FragmentResultAttributes['endEnzymeID'] {
    return this.get('endEnzymeID');
  }

  originalBases(): FragmentResultAttributes['originalBases'] {
    return this.get('originalBases');
  }

  parent(): FragmentResultAttributes['parent'] {
    return this.get('parent');
  }

  parentVersion(): FragmentResultAttributes['parentVersion'] {
    return this.get('parentVersion');
  }

  enzymeFlanked(): boolean {
    return this.startEnzymeID() != null && this.endEnzymeID() != null;
  }

  primerFlanked(): boolean {
    return !this.enzymeFlanked() && this.primerPairID() != null;
  }

  override parse(resp: ServerFragmentResult): FragmentResultAttributes {
    // eslint-disable-next-line no-restricted-syntax
    _.extend(resp, {
      fragmentID: resp.fragment_id,
      parentVersion: resp.parent_version,
      primerPairID: resp.primer_pair_id,
      startEnzymeID: resp.start_enzyme_id,
      endEnzymeID: resp.end_enzyme_id,
      versionID: resp.version_id,
      originalBases: resp.original_bases,
    });

    // eslint-disable-next-line no-restricted-syntax
    return _.omit(
      resp,
      'fragment_id',
      'parent_version',
      'primer_pair_id',
      'start_enzyme_id',
      'end_enzyme_id',
      'version_id',
      'original_bases'
    );
  }

  override toJSON(): ServerFragmentResult {
    const json = super.toJSON();
    // eslint-disable-next-line no-restricted-syntax
    _.extend(json, {
      fragment_id: json.fragmentID,
      primer_pair_id: json.primerPairID,
      start_enzyme_id: json.startEnzymeID,
      end_enzyme_id: json.endEnzymeID,
      version_id: json.versionID,
      pair_index: json.pairIndex,
      original_bases: json.originalBases,
    });

    // eslint-disable-next-line no-restricted-syntax
    return _.omit(
      json,
      'fragmentID',
      'primerPairID',
      'startEnzymeID',
      'endEnzymeID',
      'parentVersion',
      'versionID',
      'pairIndex',
      'parent',
      'originalBases'
    );
  }
}

class FragmentResults extends BenchlingCollection<FragmentResult, FragmentResultAttributes> {
  static initClass(): void {
    this.prototype.model = FragmentResult;
    this.prototype.comparator = 'index';
  }

  override parse(resp: Array<ServerFragmentResult>): Array<FragmentResult> {
    // eslint-disable-next-line no-restricted-syntax
    return _.map(resp, (fragmentResult) => new FragmentResult(fragmentResult, {parse: true}));
  }
}
FragmentResults.initClass();

export {FragmentResult as Model, FragmentResults as Collection};
